import api from "@/lib/api";
import { logDev } from "@/lib/log";

// Convert the VAPID public key (base64url) into the Uint8Array format
// that `PushManager.subscribe` requires.
function urlBase64ToUint8Array(base64String) {
  const padding = "=".repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, "+").replace(/_/g, "/");
  const raw = window.atob(base64);
  const output = new Uint8Array(raw.length);
  for (let i = 0; i < raw.length; ++i) output[i] = raw.charCodeAt(i);
  return output;
}

export function isPushSupported() {
  return typeof window !== "undefined"
    && "serviceWorker" in navigator
    && "PushManager" in window
    && "Notification" in window;
}

export async function getPushPermission() {
  if (!isPushSupported()) return "unsupported";
  return Notification.permission; // "default" | "granted" | "denied"
}

export async function getCurrentSubscription() {
  if (!isPushSupported()) return null;
  try {
    const reg = await navigator.serviceWorker.getRegistration("/service-worker.js");
    if (!reg) return null;
    return await reg.pushManager.getSubscription();
  } catch (err) { logDev(err); return null; }
}

export async function enablePush() {
  if (!isPushSupported()) throw new Error("Push tidak disokong pada browser ini.");
  // Register (or reuse) the service worker.
  const reg = await navigator.serviceWorker.register("/service-worker.js");
  await navigator.serviceWorker.ready;
  // Ask permission.
  const perm = await Notification.requestPermission();
  if (perm !== "granted") throw new Error("Permission ditolak.");
  // Fetch VAPID public key from backend.
  const { data: keyResp } = await api.get("/push/vapid-key");
  const applicationServerKey = urlBase64ToUint8Array(keyResp.public_key);
  // Reuse existing subscription if any, else create.
  let sub = await reg.pushManager.getSubscription();
  if (!sub) {
    sub = await reg.pushManager.subscribe({ userVisibleOnly: true, applicationServerKey });
  }
  // Register with backend.
  const payload = sub.toJSON();
  await api.post("/push/subscribe", {
    endpoint: payload.endpoint,
    keys: payload.keys,
  });
  return sub;
}

export async function disablePush() {
  const sub = await getCurrentSubscription();
  if (!sub) return;
  try { await api.delete("/push/subscribe", { data: { endpoint: sub.endpoint } }); }
  catch (err) { logDev(err); }
  await sub.unsubscribe();
}
